import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchMyClinicSettings,
  updateMyClinicSettings,
  fetchAdminClinics,
  updateAdminClinic,
} from "@services/clinicUsers";
import { useClinicScope } from "@context/ClinicContext";
import OpeningHoursEditor from "@components/admin-clinics/OpeningHoursEditor";
import CalendarOverrides from "@components/controlPanel/CalendarOverrides";

/** Every shape the backend has sent for GET /clinic/clinics. */
function rowsOf(res) {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.clinics)) return res.clinics;
  if (Array.isArray(res)) return res;
  return [];
}

/**
 * Opening hours and the welcome message for one clinic. A clinic user edits
 * their own through /clinic/my-settings. An admin with a clinic selected
 * edits that clinic through the admin routes; without `clinicId` the admin
 * routes are never used, so nothing here can silently edit the admin's own
 * clinic while the tab row names another.
 */
export default function ControlPanelTab({ clinicId }) {
  const { clinics } = useClinicScope();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clinicName, setClinicName] = useState("");

  // Opening hours state
  const [openingHours, setOpeningHours] = useState({});
  const [originalOpeningHours, setOriginalOpeningHours] = useState({});
  const [savingHours, setSavingHours] = useState(false);
  const [hoursSuccess, setHoursSuccess] = useState(false);

  // Welcome message state
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [originalWelcomeMessage, setOriginalWelcomeMessage] = useState("");
  const [savingMessage, setSavingMessage] = useState(false);
  const [messageSuccess, setMessageSuccess] = useState(false);

  // Internal tab state for Opening Hours card
  const [openingHoursTab, setOpeningHoursTab] = useState("schedule");

  // The scope's list is read on each load, but a change to it must not
  // reload the panel underneath an edit; only the clinic itself does.
  const clinicsRef = useRef(clinics);
  clinicsRef.current = clinics;

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (clinicId) {
        const known = clinicsRef.current.find((c) => c.id === clinicId);
        const row = known || rowsOf(await fetchAdminClinics()).find((c) => c.id === clinicId);
        if (!row) throw new Error("That clinic could not be found");
        data = row;
      } else {
        const response = await fetchMyClinicSettings();
        data = response?.data || response;
      }
      setClinicName(data.clinic_name || "");
      setOpeningHours(data.opening_hours || {});
      setOriginalOpeningHours(data.opening_hours || {});
      setWelcomeMessage(data.welcome_message || "");
      setOriginalWelcomeMessage(data.welcome_message || "");
    } catch (err) {
      setError(err.message || "Failed to load clinic settings");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // The payload shape is the same on both routes.
  const save = (payload) => (
    clinicId ? updateAdminClinic(clinicId, payload) : updateMyClinicSettings(payload)
  );

  const handleSaveOpeningHours = async () => {
    setSavingHours(true);
    setHoursSuccess(false);
    try {
      await save({ opening_hours: openingHours });
      setOriginalOpeningHours(openingHours);
      setHoursSuccess(true);
      setTimeout(() => setHoursSuccess(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to save opening hours");
    } finally {
      setSavingHours(false);
    }
  };

  const handleSaveWelcomeMessage = async () => {
    setSavingMessage(true);
    setMessageSuccess(false);
    try {
      await save({ welcome_message: welcomeMessage });
      setOriginalWelcomeMessage(welcomeMessage);
      setMessageSuccess(true);
      setTimeout(() => setMessageSuccess(false), 3000);
    } catch (err) {
      setError(err.message || "Failed to save welcome message");
    } finally {
      setSavingMessage(false);
    }
  };

  const hoursChanged = JSON.stringify(openingHours) !== JSON.stringify(originalOpeningHours);
  const messageChanged = welcomeMessage !== originalWelcomeMessage;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3 text-gray-500">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Loading settings...</span>
        </div>
      </div>
    );
  }

  if (error && !openingHours && !welcomeMessage) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="text-red-600 text-sm">{error}</div>
        <button
          onClick={loadSettings}
          className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-8 w-full py-4 pb-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">Control Panel</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage settings for {clinicName || "your clinic"}
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 max-w-5xl w-full">
          {error}
        </div>
      )}

      {/* Cards grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl w-full">
        {/* Opening Hours Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 transition-all duration-300 hover:shadow-lg hover:border-teal-200">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
              <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Opening Hours</h2>
              <p className="text-xs text-gray-500">Set your clinic's operating schedule</p>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            {/* Internal tabs */}
            <div className="flex gap-1 mb-4 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setOpeningHoursTab("schedule")}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  openingHoursTab === "schedule"
                    ? "border-teal-600 text-teal-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Weekly Schedule
              </button>
              <button
                type="button"
                onClick={() => setOpeningHoursTab("overrides")}
                className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  openingHoursTab === "overrides"
                    ? "border-teal-600 text-teal-700"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Calendar Overrides
              </button>
            </div>

            {/* Tab content */}
            {openingHoursTab === "schedule" ? (
              <OpeningHoursEditor value={openingHours} onChange={setOpeningHours} />
            ) : (
              <CalendarOverrides
                defaultOpeningHours={openingHours}
                onOverridesChange={() => {}}
              />
            )}
          </div>
          <div className={`px-4 sm:px-5 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between ${openingHoursTab !== "schedule" ? "hidden" : ""}`}>
            {hoursSuccess && (
              <span className="text-sm text-green-600 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Saved successfully
              </span>
            )}
            {!hoursSuccess && <span />}
            <button
              onClick={handleSaveOpeningHours}
              disabled={!hoursChanged || savingHours}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                hoursChanged && !savingHours
                  ? "bg-gray-900 text-white hover:bg-gray-800"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {savingHours ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {/* Welcome Message Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-blue-200">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Welcome Message</h2>
              <p className="text-xs text-gray-500">Customize your AI assistant's greeting</p>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            <textarea
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              placeholder="Enter the welcome message your AI assistant will use when greeting callers..."
              rows={8}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition resize-none"
            />
            <p className="mt-2 text-xs text-gray-400">
              This message will be used by your AI phone assistant when answering calls.
            </p>
          </div>
          <div className="px-4 sm:px-5 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            {messageSuccess && (
              <span className="text-sm text-green-600 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Saved successfully
              </span>
            )}
            {!messageSuccess && <span />}
            <button
              onClick={handleSaveWelcomeMessage}
              disabled={!messageChanged || savingMessage}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                messageChanged && !savingMessage
                  ? "bg-gray-900 text-white hover:bg-gray-800"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {savingMessage ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
